import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import accountsPayableService from '../services/accountsPayableService';
import {
  DollarSign, Clock, CheckCircle, AlertTriangle, X, Loader,
  Calendar, Package, Building, RotateCcw, Search
} from 'lucide-react';
import toast from 'react-hot-toast';

const AccountsPayable = ({ projectId, projectName, onClose }) => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('PENDING');
  const [searchTerm, setSearchTerm] = useState('');
  const [installmentToPay, setInstallmentToPay] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Transferência',
    notes: ''
  });

  const isModal = Boolean(onClose);

  // Buscar parcelas da OC específica ou todas (BUG #17 fix: usar projectId corretamente)
  const { data: installmentsData, isLoading } = useQuery({
    queryKey: ['accounts-payable-installments', projectId],
    queryFn: () => projectId
      ? accountsPayableService.getPendingInstallments({ project_id: projectId })
      : accountsPayableService.getPendingInstallments(),
    enabled: true
  });

  // Mutation para pagar parcela
  const payMutation = useMutation({
    mutationFn: ({ installmentId, data }) => accountsPayableService.payInstallment(installmentId, data),
    onSuccess: () => {
      toast.success('Pagamento registrado com sucesso!');
      queryClient.invalidateQueries(['accounts-payable-installments', projectId]);
      setInstallmentToPay(null);
      setPaymentData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Transferência',
        notes: ''
      });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao registrar pagamento');
    }
  });

  // Mutation para estornar pagamento
  const revertMutation = useMutation({
    mutationFn: ({ installmentId, reason }) => accountsPayableService.revertPayment(installmentId, reason),
    onSuccess: () => {
      toast.success('Pagamento estornado com sucesso! Saldo de caixa restaurado.');
      queryClient.invalidateQueries(['accounts-payable-installments', projectId]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao estornar pagamento');
    }
  });

  const handleRevert = (installment) => {
    const reason = window.prompt(
      `Confirma o ESTORNO do pagamento da Parcela ${installment.installment_number} (${formatCurrency(installment.amount)})?\n\nInforme o motivo do estorno:`,
      'Estorno solicitado pelo operador'
    );
    if (reason !== null) {
      revertMutation.mutate({ installmentId: installment.id, reason });
    }
  };

  const installments = installmentsData?.data || [];
  const summary = installmentsData?.summary || { total_pending: 0, total_paid: 0, count_pending: 0, count_paid: 0 };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (month, year) => {
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' });
  };

  const isOverdue = (month, year) => {
    const dueDate = new Date(year, month - 1, 1);
    const today = new Date();
    today.setDate(1);
    return dueDate < today;
  };

  const filteredInstallments = installments.filter(inst => {
    let matchesFilter = true;
    if (filter === 'OVERDUE') matchesFilter = inst.status === 'PENDING' && isOverdue(inst.due_month, inst.due_year);
    else if (filter !== 'ALL') matchesFilter = inst.status === filter;

    if (!matchesFilter) return false;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        (inst.supplier_name && inst.supplier_name.toLowerCase().includes(term)) ||
        (inst.description && inst.description.toLowerCase().includes(term)) ||
        (inst.purchase_order_id && String(inst.purchase_order_id).includes(term)) ||
        (inst.order_number && inst.order_number.toLowerCase().includes(term));
      return matchesSearch;
    }

    return true;
  });

  const handleOpenPayModal = (installment) => {
    setInstallmentToPay(installment);
    setPaymentData({
      amount: installment.amount,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'Transferência',
      notes: ''
    });
  };

  const handlePay = () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Informe o valor a pagar');
      return;
    }
    payMutation.mutate({
      installmentId: installmentToPay.id,
      data: {
        amount: parseFloat(paymentData.amount),
        payment_date: paymentData.payment_date,
        payment_method: paymentData.payment_method,
        notes: paymentData.notes
      }
    });
  };

  return (
    <div className={isModal ? "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" : "p-6 max-w-7xl mx-auto space-y-6"}>
      <div className={isModal ? "bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" : "bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col"}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-red-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isModal ? 'Contas a Pagar do Projeto' : 'Contas a Pagar & Boletos de Fornecedores'}
              </h2>
              {projectName ? (
                <p className="text-sm text-gray-600">{projectName}</p>
              ) : (
                <p className="text-xs text-gray-500">Gestão integrada de duplicatas, ordens de compra e despesas da empresa</p>
              )}
            </div>
          </div>
          {isModal && (
            <button onClick={onClose} className="p-2 hover:bg-red-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 border-b">
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <Clock className="w-4 h-4 text-yellow-600" />
              Pendentes
            </div>
            <p className="text-xl font-bold text-yellow-600">{formatCurrency(summary.total_pending)}</p>
            <p className="text-xs text-gray-500">{summary.count_pending} parcela(s)</p>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Pagas
            </div>
            <p className="text-xl font-bold text-green-600">{formatCurrency(summary.total_paid)}</p>
            <p className="text-xs text-gray-500">{summary.count_paid} parcela(s)</p>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              Vencidas
            </div>
            <p className="text-xl font-bold text-red-600">
              {formatCurrency(
                installments
                  .filter(i => i.status === 'PENDING' && isOverdue(i.due_month, i.due_year))
                  .reduce((sum, i) => sum + parseFloat(i.amount), 0)
              )}
            </p>
            <p className="text-xs text-gray-500">
              {installments.filter(i => i.status === 'PENDING' && isOverdue(i.due_month, i.due_year)).length} parcela(s)
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <DollarSign className="w-4 h-4 text-gray-700" />
              Total Geral
            </div>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(summary.total_pending + summary.total_paid)}
            </p>
            <p className="text-xs text-gray-500">{installments.length} parcela(s)</p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 border-b bg-white">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'PENDING', label: 'Pendentes' },
              { key: 'OVERDUE', label: 'Vencidas' },
              { key: 'PAID', label: 'Pagas' },
              { key: 'ALL', label: 'Todas' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === f.key
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative min-w-[260px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar fornecedor, OC, despesa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : filteredInstallments.length === 0 ? (
            <div className="text-center p-8 text-gray-500">
              Nenhuma parcela encontrada.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredInstallments.map(inst => {
                const overdue = inst.status === 'PENDING' && isOverdue(inst.due_month, inst.due_year);
                return (
                  <div
                    key={inst.id}
                    className={`border rounded-lg p-4 ${
                      inst.status === 'PAID'
                        ? 'bg-green-50 border-green-200'
                        : overdue
                          ? 'bg-red-50 border-red-200'
                          : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          inst.status === 'PAID'
                            ? 'bg-green-100 text-green-600'
                            : overdue
                              ? 'bg-red-100 text-red-600'
                              : 'bg-yellow-100 text-yellow-600'
                        }`}>
                          {inst.status === 'PAID' ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : overdue ? (
                            <AlertTriangle className="w-5 h-5" />
                          ) : (
                            <Clock className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              Parcela {inst.installment_number}/{inst.installments_count || inst.installment_number}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              inst.status === 'PAID'
                                ? 'bg-green-100 text-green-700'
                                : overdue
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {inst.status === 'PAID' ? 'PAGA' : overdue ? 'VENCIDA' : 'PENDENTE'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {inst.supplier_name || 'Fornecedor'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Vencimento: {formatDate(inst.due_month, inst.due_year)}
                            </span>
                            {inst.order_number && (
                              <span className="flex items-center gap-1">
                                <Package className="w-3 h-3" />
                                OC #{inst.purchase_order_id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">
                            {formatCurrency(inst.amount)}
                          </p>
                          {inst.status === 'PAID' && inst.paid_at && (
                            <p className="text-xs text-green-600">
                              Pago em: {new Date(inst.paid_at).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </div>
                        {inst.status !== 'PAID' ? (
                          <button
                            onClick={() => handleOpenPayModal(inst)}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium shadow-sm"
                          >
                            Baixar
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRevert(inst)}
                            disabled={revertMutation.isLoading}
                            className="flex items-center gap-1 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-100 text-sm font-medium transition-colors"
                            title="Estornar pagamento da parcela"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Estornar
                          </button>
                        )}
                      </div>
                    </div>
                    {inst.description && (
                      <p className="mt-2 text-sm text-gray-500 pl-10">
                        {inst.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {isModal && (
          <div className="p-4 border-t bg-gray-50 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Fechar
            </button>
          </div>
        )}

        {/* Pay Installment Modal */}
        {installmentToPay && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">Baixar Parcela</h3>
                <button onClick={() => setInstallmentToPay(null)} className="p-2 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">
                    Parcela {installmentToPay.installment_number}/{installmentToPay.installments_count || '?'}
                  </p>
                  <p className="font-medium text-gray-900">
                    {installmentToPay.supplier_name || 'Fornecedor'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Vencimento: {formatDate(installmentToPay.due_month, installmentToPay.due_year)}
                  </p>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    Valor: {formatCurrency(parseFloat(installmentToPay.amount))}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor a Pagar
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data do Pagamento
                  </label>
                  <input
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Método de Pagamento
                  </label>
                  <select
                    value={paymentData.payment_method}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, payment_method: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="Transferência">Transferência</option>
                    <option value="PIX">PIX</option>
                    <option value="Dinheiro">Dinheiro</option>
                    <option value="Boleto">Boleto</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Cartão">Cartão</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={2}
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => setInstallmentToPay(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePay}
                  disabled={payMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {payMutation.isPending && <Loader className="w-4 h-4 animate-spin" />}
                  Confirmar Pagamento
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsPayable;